import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, 
  Search, 
  Calendar, 
  MessageSquare, 
  Bookmark,
  Mail,
  Clock,
  Star,
  Plus,
  Sliders,
  X,
  ExternalLink,
  User,
  Settings,
  LogOut,
  Crown
  , ChevronDown
} from 'lucide-react';
import Avatar from './Avatar.tsx';
import GoogleCalendarModal from './modals/GoogleCalendarModal.tsx';
import UpgradeModal from './modals/UpgradeModal.tsx';
import CostBudgetPopover from './AIConfig/CostBudgetPopover.jsx';
import useCostSummary, { clearCostSummary } from '../hooks/useCostSummary.ts';
import { agentRunAPI, remindersAPI, spacesAPI } from '../services/api.js';
// Domains modal removed — domain editing moved to Onboarding
import { getCurrentUserProfile, formatUserProfile } from '../utils/auth.ts';

interface TopBarProps {
  isDarkMode?: boolean;
  onIconClick?: (iconName: string) => void;
  onSearchResults?: (results: SearchResult[]) => void;
  onLogout?: () => void;
}

interface SearchResult {
  label: string;
  path: string;
  icon: string;
  category: string;
}

export default function TopBar({ isDarkMode = false, onIconClick, onSearchResults, onLogout }: TopBarProps) {
  const navigate = useNavigate();
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showRemindersMenu, setShowRemindersMenu] = useState(false);
  const [reminders, setReminders] = useState<any[]>([]);
  const [remindersLoading, setRemindersLoading] = useState(false);
  const [seenReminderKeys, setSeenReminderKeys] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem('lyra_seen_reminders');
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  });
  const [stoppingReminderRunIds, setStoppingReminderRunIds] = useState<Set<string>>(new Set());
  const [acceptingReminderSpaceIds, setAcceptingReminderSpaceIds] = useState<Set<string>>(new Set());
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const remindersMenuRef = useRef<HTMLDivElement>(null);
  const pillRef = useRef<HTMLButtonElement>(null);
  const [currency, setCurrency] = useState(localStorage.getItem('ai_currency') || 'USD');
  const { data: costData, loading: costLoading, hasData: costHasData, refresh: fetchCostSummary } = useCostSummary();
  const [showCostPop, setShowCostPop] = useState(false);
  const API_BASE = process.env.REACT_APP_API_URL ?? '';

  // Get current user profile
  const userProfile = getCurrentUserProfile();
  const formattedProfile = userProfile ? formatUserProfile(userProfile) : null;

  // Menu configuration for search (you can import this from your hook or context)
  const getMenuItems = () => {
    // Import the actual menu configuration
    try {
      // This is a simplified version - you should use your actual menu config
      const menuConfig = require('../config/menuConfig.json');
      return menuConfig.menuConfig;
    } catch (error) {
      console.error('Error loading menu config:', error);
      return {};
    }
  };

  const searchMenuItems = (query: string): SearchResult[] => {
    if (!query.trim()) return [];
    
    const results: SearchResult[] = [];
    const menuItems = getMenuItems();
    const queryLower = query.toLowerCase();

    const searchInLevel = (items: any, category: string = '') => {
      Object.entries(items).forEach(([key, item]: [string, any]) => {
        if (item.children) {
          // If it has children, search recursively
          searchInLevel(item.children, item.label || category);
        } else if (item.visible && item.path && item.label) {
          // Check if this is a leaf item with a path
          const labelWords = item.label.toLowerCase().split(' ');
          const queryWords = queryLower.split(' ');
          
          // Check if any query word matches any label word
          const hasMatch = queryWords.some(queryWord => 
            labelWords.some(labelWord => 
              labelWord.includes(queryWord) || queryWord.includes(labelWord)
            )
          );

          if (hasMatch) {
            results.push({
              label: item.label,
              path: item.path,
              icon: item.icon,
              category: category
            });
          }
        }
      });
    };

    searchInLevel(menuItems);
    return results;
  };

  const handleSearchClick = () => {
    setIsSearchExpanded(!isSearchExpanded);
    if (!isSearchExpanded) {
      // Focus the input when expanding
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    } else {
      // Clear search when collapsing
      setSearchQuery('');
      setSearchResults([]);
      setShowResults(false);
      if (onSearchResults) {
        onSearchResults([]);
      }
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    const results = searchMenuItems(query);
    setSearchResults(results);
    setShowResults(query.length > 0 && results.length > 0);
    
    if (onSearchResults) {
      onSearchResults(results);
    }
  };

  const handleSearchClear = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
    if (onSearchResults) {
      onSearchResults([]);
    }
    searchInputRef.current?.focus();
  };

  const handleResultClick = (result: SearchResult) => {
    navigate(result.path);
    setIsSearchExpanded(false);
    setSearchQuery('');
    setSearchResults([]);
    setShowResults(false);
    if (onSearchResults) {
      onSearchResults([]);
    }
  };

  // Close search when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isSearchExpanded && searchContainerRef.current && !searchContainerRef.current.contains(event.target as Node)) {
        setIsSearchExpanded(false);
        setSearchQuery('');
        setSearchResults([]);
        setShowResults(false);
        if (onSearchResults) {
          onSearchResults([]);
        }
      }
      
      if (showProfileMenu && profileMenuRef.current && !profileMenuRef.current.contains(event.target as Node)) {
        setShowProfileMenu(false);
      }

      if (showRemindersMenu && remindersMenuRef.current && !remindersMenuRef.current.contains(event.target as Node)) {
        setShowRemindersMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isSearchExpanded, showProfileMenu, showRemindersMenu, onSearchResults]);

  const handleIconClick = (iconName: string) => {
    if (iconName === 'search') {
      handleSearchClick();
      return;
    }
    if (iconName === 'reminders') {
      setShowRemindersMenu((v) => !v);
      return;
    }
    if (iconName === 'calendar') {
      setShowCalendarModal(false);
      return;
    }
    
    console.log(`Clicked on ${iconName} icon`);
    if (onIconClick) {
      onIconClick(iconName);
    }
  };

  const handleProfileClick = () => {
    setShowProfileMenu(!showProfileMenu);
  };

  const handleProfileMenuClick = (action: string) => {
    setShowProfileMenu(false);
    
    switch (action) {
      case 'profile':
        navigate('/settings/profile');
        break;
      case 'settings':
        navigate('/settings/menu-customization');
        break;
      case 'subscribed':
        setShowUpgradeModal(true);
        break;
      case 'logout':
        if (onLogout) {
          onLogout();
        } else {
          // Fallback if onLogout prop is not provided
          localStorage.removeItem('authToken');
        clearCostSummary();
          localStorage.removeItem('googleAuthToken');
          window.location.href = '/';
        }
        break;
      default:
        break;
    }
  };

  useEffect(() => { localStorage.setItem('ai_currency', currency); }, [currency]);

  useEffect(() => {
    try {
      localStorage.setItem('lyra_seen_reminders', JSON.stringify(seenReminderKeys.slice(0, 500)));
    } catch (e) {
      // ignore storage write errors
    }
  }, [seenReminderKeys]);

  const refreshReminders = async (loadingState = false) => {
    try {
      if (loadingState) setRemindersLoading(true);
      const capUsd = Number(localStorage.getItem('ai_cap') || 50);
      const seen = seenReminderKeys.join(',');
      const data = await remindersAPI.list({ sinceMinutes: 180, capUsd, seen });
      if (data?.success) {
        setReminders(Array.isArray(data.reminders) ? data.reminders : []);
      }
    } catch (e) {
      // ignore failed reminder fetches to avoid noisy top bar UX
    } finally {
      if (loadingState) setRemindersLoading(false);
    }
  };

  useEffect(() => {
    refreshReminders(true);
    const timer = window.setInterval(() => {
      refreshReminders(false);
    }, 30000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (showRemindersMenu) {
      refreshReminders(true);
    }
  }, [showRemindersMenu]);

  const markReminderSeen = (eventKey: string) => {
    if (!eventKey) return;
    setSeenReminderKeys((prev) => {
      if (prev.includes(eventKey)) return prev;
      return [...prev, eventKey];
    });
    setReminders((prev) => prev.filter((r) => r.eventKey !== eventKey));
  };

  const clearAllReminders = () => {
    const keys = reminders.map((r) => r.eventKey).filter(Boolean);
    if (keys.length === 0) return;
    setSeenReminderKeys((prev) => [...prev, ...keys.filter((k) => !prev.includes(k))]);
    setReminders([]);
  };

  const handleReminderPrimaryAction = async (reminder: any) => {
    if (!reminder) return;
    const stopAction = Array.isArray(reminder.actions)
      ? reminder.actions.find((a: any) => a?.type === 'stop_run' && a?.runId)
      : null;

    if (stopAction?.runId) {
      const runId = String(stopAction.runId);
      setStoppingReminderRunIds((prev) => {
        const next = new Set(prev);
        next.add(runId);
        return next;
      });
      try {
        await agentRunAPI.stop(runId);
      } catch (e) {
        // best effort
      } finally {
        setStoppingReminderRunIds((prev) => {
          const next = new Set(prev);
          next.delete(runId);
          return next;
        });
      }
    }

    if (reminder.link) {
      navigate(reminder.link);
      setShowRemindersMenu(false);
    }
    markReminderSeen(reminder.eventKey);
  };

  const handleAcceptInvite = async (reminder: any) => {
    const acceptAction = Array.isArray(reminder?.actions)
      ? reminder.actions.find((a: any) => a?.type === 'accept_invite' && a?.spaceId)
      : null;
    if (!acceptAction) return;
    const spaceId = String(acceptAction.spaceId);
    setAcceptingReminderSpaceIds((prev) => new Set(prev).add(spaceId));
    try {
      await spacesAPI.join(spaceId);
    } catch (e) {
      // best effort — leave the reminder visible so the user can retry
    } finally {
      setAcceptingReminderSpaceIds((prev) => {
        const next = new Set(prev);
        next.delete(spaceId);
        return next;
      });
    }
    markReminderSeen(reminder.eventKey);
  };

  const handleDeclineInvite = async (reminder: any) => {
    const acceptAction = Array.isArray(reminder?.actions)
      ? reminder.actions.find((a: any) => a?.type === 'accept_invite' && a?.spaceId && a?.inviteId)
      : null;
    if (!acceptAction) return;
    const spaceId = String(acceptAction.spaceId);
    setAcceptingReminderSpaceIds((prev) => new Set(prev).add(spaceId));
    try {
      await spacesAPI.declineInvite(spaceId, String(acceptAction.inviteId));
    } catch (e) {
      // best effort — leave the reminder visible so the user can retry
    } finally {
      setAcceptingReminderSpaceIds((prev) => {
        const next = new Set(prev);
        next.delete(spaceId);
        return next;
      });
    }
    markReminderSeen(reminder.eventKey);
  };

  // Notifications component will be defined below once helper vars are declared

  const iconButtonClass = `
    flex items-center justify-center p-2 rounded-md transition-colors duration-150 group
    ${isDarkMode 
      ? 'text-gray-300 hover:bg-gray-700 hover:text-white' 
      : 'text-gray-500 hover:bg-gray-100 hover:text-gray-800'
    }
  `;

  const topBarIcons = [
    { name: 'search', icon: Search, label: 'Search', tooltip: 'Search' },
    { name: 'reminders', icon: Bell, label: 'Reminders', tooltip: 'Reminders' },
    // { name: 'calendar', icon: Calendar, label: 'Calendar', tooltip: 'Calendar' },
    // { name: 'messages', icon: MessageSquare, label: 'Messages', tooltip: 'Messages' },
    // { name: 'mail', icon: Mail, label: 'Mail', tooltip: 'Mail' },
  ];

  return (
    <>
      <div className={`
        h-14 px-5 flex items-center justify-between border-b transition-colors relative
        ${isDarkMode 
          ? 'bg-gray-800 border-gray-700' 
          : 'bg-white border-gray-200'
        }
      `}>
        {/* Spacer: pushes search and the account controls to the right */}
        <div style={{ flex: 1 }} />

        {/* Right-aligned Search field and icons container */}
        <div className="flex items-center space-x-1.5">
          {/* Search field (expandable to the left) */}
          <div 
            ref={searchContainerRef}
            className={`
              flex flex-col relative transition-all duration-300 ease-in-out
              ${isSearchExpanded ? 'w-80 max-w-sm mr-2' : 'w-0'}
            `}
          >
            {/* Search Input */}
            <div className={`
              flex items-center space-x-2 px-3 py-2 rounded-md border transition-all duration-300 ease-in-out overflow-hidden
              ${isDarkMode 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300 text-gray-900'
              }
              ${isSearchExpanded ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}
            `}>
              <Search size={16} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
              <input
                ref={searchInputRef}
                type="text"
                placeholder="What are you looking for..."
                value={searchQuery}
                onChange={handleSearchChange}
                className={`
                  flex-1 bg-transparent outline-none text-sm transition-all duration-300
                  ${isDarkMode ? 'placeholder-gray-400' : 'placeholder-gray-500'}
                  ${isSearchExpanded ? 'w-full' : 'w-0'}
                `}
              />
              {searchQuery && isSearchExpanded && (
                <button
                  onClick={handleSearchClear}
                  className={`p-1 rounded-full transition-colors ${isDarkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-100'}`}
                >
                  <X size={14} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
                </button>
              )}
            </div>
            
            {/* Search Results Dropdown */}
            {isSearchExpanded && showResults && searchResults.length > 0 && (
              <div className={`
                absolute top-full left-0 right-0 mt-1 rounded-lg border shadow-lg z-50 max-h-96 overflow-y-auto
                animate-in fade-in slide-in-from-top-2 duration-200
                ${isDarkMode 
                  ? 'bg-gray-700 border-gray-600' 
                  : 'bg-white border-gray-300'
                }
              `}>
                {searchResults.map((result, index) => (
                  <div
                    key={index}
                    onClick={() => handleResultClick(result)}
                    className={`
                      flex items-center space-x-3 px-4 py-3 cursor-pointer border-b last:border-b-0 transition-colors
                      ${isDarkMode 
                        ? 'hover:bg-gray-600 border-gray-600 text-gray-200' 
                        : 'hover:bg-gray-50 border-gray-200 text-gray-800'
                      }
                    `}
                  >
                    <ExternalLink size={16} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
                    <div className="flex-1">
                      <div className="font-medium text-sm">{result.label}</div>
                      {result.category && (
                        <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {result.category}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* No Results Message */}
            {isSearchExpanded && showResults && searchResults.length === 0 && searchQuery.length > 0 && (
              <div className={`
                absolute top-full left-0 right-0 mt-1 rounded-lg border shadow-lg z-50 px-4 py-3
                animate-in fade-in slide-in-from-top-2 duration-200
                ${isDarkMode 
                  ? 'bg-gray-700 border-gray-600 text-gray-400' 
                  : 'bg-white border-gray-300 text-gray-500'
                }
              `}>
                No menu items found for "{searchQuery}"
              </div>
            )}
          </div>
          
          {/* Action icons */}
          <div className="flex items-center space-x-1">
            {topBarIcons.map(({ name, icon: IconComponent, label, tooltip }, idx) => (
              <React.Fragment key={name}>
                <button
                  onClick={() => handleIconClick(name)}
                  className={`
                    ${iconButtonClass}
                    ${name === 'search' && isSearchExpanded ? (isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-800') : ''}
                    ${name === 'reminders' && showRemindersMenu ? (isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-800') : ''}
                    h-9 w-9
                  `}
                  title={tooltip}
                  aria-label={tooltip}
                >
                  <div className="relative">
                    <IconComponent size={15} />
                    {name === 'reminders' && reminders.length > 0 ? (
                      <span className="absolute -top-2 -right-2 h-5 min-w-5 px-1.5 bg-red-500 rounded-full flex items-center justify-center text-white text-xs font-bold leading-none">
                        {reminders.length > 9 ? '9+' : reminders.length}
                      </span>
                    ) : null}
                  </div>
                </button>

                {name === 'reminders' && showRemindersMenu && (
                  <div
                    ref={remindersMenuRef}
                    className={`
                      absolute top-14 right-0 mt-1 w-[360px] max-w-[92vw] rounded-xl border shadow-lg z-50
                      ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}
                    `}
                  >
                    <div className={`px-3 py-2 border-b flex items-center justify-between ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <span className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Reminders</span>
                      <div className="flex items-center gap-1.5">
                        {reminders.length > 0 && (
                          <button
                            onClick={clearAllReminders}
                            className={`text-xs px-2 py-1 rounded border font-medium ${isDarkMode ? 'text-red-400 border-gray-700 hover:bg-red-950/40' : 'text-red-600 border-red-200 bg-red-50 hover:bg-red-100'}`}
                          >
                            Clear all
                          </button>
                        )}
                        <button
                          onClick={() => refreshReminders(true)}
                          className={`text-xs px-2 py-1 rounded border font-medium ${isDarkMode ? 'text-gray-300 border-gray-700 hover:bg-gray-700' : 'text-gray-600 border-gray-200 hover:bg-gray-100'}`}
                        >
                          Refresh
                        </button>
                      </div>
                    </div>
                    <div className="max-h-[420px] overflow-y-auto">
                      {remindersLoading ? (
                        <div className={`px-3 py-4 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>Loading reminders...</div>
                      ) : reminders.length === 0 ? (
                        <div className={`px-3 py-4 text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>No reminders right now.</div>
                      ) : (
                        reminders.map((reminder, index) => {
                          const stopAction = Array.isArray(reminder.actions)
                            ? reminder.actions.find((a: any) => a?.type === 'stop_run' && a?.runId)
                            : null;
                          const stopping = stopAction?.runId ? stoppingReminderRunIds.has(String(stopAction.runId)) : false;
                          const acceptAction = Array.isArray(reminder.actions)
                            ? reminder.actions.find((a: any) => a?.type === 'accept_invite' && a?.spaceId)
                            : null;
                          const accepting = acceptAction?.spaceId ? acceptingReminderSpaceIds.has(String(acceptAction.spaceId)) : false;
                          return (
                            <div
                              key={`${reminder.eventKey || 'rem'}-${index}`}
                              className={`px-3 py-3 border-b last:border-b-0 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                  <div className={`text-sm font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                                    {reminder.title || 'Reminder'}
                                  </div>
                                  <div className={`text-xs mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                    {reminder.message}
                                  </div>
                                </div>
                                <button
                                  onClick={() => markReminderSeen(reminder.eventKey)}
                                  className={`text-[11px] px-2 py-1 rounded ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`}
                                >
                                  Dismiss
                                </button>
                              </div>
                              <div className="mt-2 flex items-center gap-2">
                                {stopAction ? (
                                  <button
                                    onClick={() => handleReminderPrimaryAction({ ...reminder, link: null })}
                                    disabled={stopping}
                                    className={`text-xs px-2 py-1 rounded font-semibold ${stopping ? 'opacity-70 cursor-not-allowed' : ''} ${isDarkMode ? 'bg-red-600 text-white hover:bg-red-500' : 'bg-red-600 text-white hover:bg-red-700'}`}
                                  >
                                    {stopping ? 'Stopping...' : 'Stop'}
                                  </button>
                                ) : null}
                                {acceptAction ? (
                                  <button
                                    onClick={() => handleAcceptInvite(reminder)}
                                    disabled={accepting}
                                    className={`text-xs px-2 py-1 rounded font-semibold ${accepting ? 'opacity-70 cursor-not-allowed' : ''} ${'bg-[color:var(--app-primary-mid)] text-white hover:bg-[color:var(--app-primary-deep)]'}`}
                                  >
                                    {accepting ? 'Accepting...' : 'Accept'}
                                  </button>
                                ) : null}
                                {acceptAction ? (
                                  <button
                                    onClick={() => handleDeclineInvite(reminder)}
                                    disabled={accepting}
                                    className={`text-xs px-2 py-1 rounded font-semibold ${accepting ? 'opacity-70 cursor-not-allowed' : ''} ${isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-600 hover:bg-gray-100'}`}
                                  >
                                    Deny
                                  </button>
                                ) : null}
                                {reminder.link ? (
                                  <button
                                    onClick={() => handleReminderPrimaryAction(reminder)}
                                    className={`text-xs px-2 py-1 rounded font-semibold ${'bg-[color:var(--app-primary-mid)] text-white hover:bg-[color:var(--app-primary-deep)]'}`}
                                  >
                                    Open
                                  </button>
                                ) : null}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}

                {/* ── Cost & Budget pill — between Search and Reminders ── */}
                {idx === 0 && (
                  <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                    <button
                      ref={pillRef}
                      onClick={() => setShowCostPop(v => { const nv = !v; if (!v) fetchCostSummary(); return nv; })}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 8,
                        padding: '5px 12px', borderRadius: 999, cursor: 'pointer',
                        border: `1px solid ${showCostPop ? ('var(--app-primary-mid)') : ('var(--lyra-color-border)')}`,
                        background: showCostPop ? ('var(--app-primary-soft)') : ('var(--lyra-color-surface)'),
                        transition: 'all 0.15s ease',
                      }}
                      title="Cost & Budget"
                    >
                      {/* Currency toggle — stops propagation so pill click doesn't re-close */}
                      <span
                        role="button"
                        onClick={e => {
                          e.stopPropagation();
                          setCurrency(c => { const nc = c === 'USD' ? 'INR' : 'USD'; return nc; });
                        }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: 3, padding: '1px 7px',
                          borderRadius: 999, border: `1px solid ${'var(--lyra-color-border)'}`,
                          background: 'var(--lyra-color-surface)',
                          cursor: 'pointer', fontSize: 11, fontWeight: 800,
                        }}
                        title="Toggle USD / INR"
                      >
                        <span style={{ opacity: currency === 'USD' ? 1 : 0.35, color: currency === 'USD' ? 'var(--app-primary-deep)' : undefined }}>$</span>
                        <span style={{ color: 'var(--app-text-3)', fontSize: 9.5 }}>|</span>
                        <span style={{ opacity: currency === 'INR' ? 1 : 0.35, color: currency === 'INR' ? 'var(--app-primary-deep)' : undefined }}>₹</span>
                      </span>

                      {/* Estimate + cap */}
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.2 }}>
                        <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--lyra-color-text)'}}>
                          {!costHasData && costLoading
                              ? '…'
                              : (() => {
                                  const ce = costData?.summary?.totalEstimatedCostUSD ?? 0;
                                  return currency === 'USD'
                                    ? `$${Number(ce).toFixed(3)}`
                                    : `₹${(Number(ce) * 83.5).toFixed(0)}`;
                                })()
                            }
                        </span>
                        <span style={{ fontSize: 9.5, color: 'var(--lyra-color-text-muted)', fontWeight: 600 }}>
                          {currency === 'USD'
                            ? `cap $${localStorage.getItem('ai_cap') ?? '50'}`
                            : `cap ₹${(Number(localStorage.getItem('ai_cap') ?? 50) * 83.5).toFixed(0)}`
                          }
                        </span>
                      </div>

                      {/* Chevron rotates when open */}
                      <span style={{ display: 'flex', transition: 'transform 0.18s', transform: showCostPop ? 'rotate(180deg)' : 'rotate(0deg)', color: 'var(--lyra-color-text-muted)' }}>
                        <ChevronDown size={12} />
                      </span>
                    </button>

                    {showCostPop && (
                      <CostBudgetPopover
                        anchorRef={pillRef}
                        onClose={() => setShowCostPop(false)}
                        currency={currency}
                        data={costData}
                        loading={costLoading}
                        onRefresh={fetchCostSummary}
                      />
                    )}
                  </div>
                )}
              </React.Fragment>
            ))}
            
            {/* Profile Picture with Dropdown */}
            {userProfile ? (
              <div className="relative" ref={profileMenuRef}>
                <button
                  onClick={handleProfileClick}
                  className={`
                    flex items-center justify-center p-1.5 rounded-md transition-colors duration-150 group
                    ${isDarkMode 
                      ? 'text-gray-300 hover:bg-gray-700 hover:text-white' 
                      : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
                    }
                    ${showProfileMenu ? (isDarkMode ? 'bg-gray-700' : 'bg-gray-100') : ''}
                    w-10 h-10
                  `}
                  title={`${userProfile.name} - Profile Menu`}
                  aria-label="Profile Menu"
                >
                  <div className="relative">
                    <Avatar src={userProfile.profilePicture} name={userProfile.name} size={28} style={{ background: 'var(--lyra-color-primary)', color: 'var(--app-on-accent)' }} />
                    {/* Online status indicator */}
                    <span className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 bg-green-500 rounded-full border border-white"></span>
                  </div>
                </button>

                {/* Profile Dropdown Menu */}
                {showProfileMenu && (
                  <div className={`
                    absolute top-full right-0 mt-2 w-52 rounded-lg border shadow-lg z-50
                    animate-in fade-in slide-in-from-top-2 duration-200
                    ${isDarkMode 
                      ? 'bg-gray-800 border-gray-700' 
                      : 'bg-white border-gray-200'
                    }
                  `}>
                    {/* User Info Header */}
                    <div className={`px-4 py-3 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                      <div className="flex items-center space-x-3">
                        <div className="relative">
                          <Avatar src={userProfile.profilePicture} name={userProfile.name} size={32} style={{ background: 'var(--lyra-color-primary)', color: 'var(--app-on-accent)' }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {userProfile.name}
                          </p>
                          <p className={`text-xs truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            {userProfile.email}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Menu Items */}
                    <div className="py-1">
                      <button
                        onClick={() => handleProfileMenuClick('profile')}
                        className={`
                          w-full flex items-center px-4 py-2 text-sm transition-colors
                          ${isDarkMode 
                            ? 'text-gray-300 hover:bg-gray-700 hover:text-white' 
                            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                          }
                        `}
                      >
                        <User className="mr-3 h-4 w-4" />
                        View Profile
                      </button>
                      
                      {/* <button
                        onClick={() => handleProfileMenuClick('settings')}
                        className={`
                          w-full flex items-center px-4 py-2 text-sm transition-colors
                          ${isDarkMode 
                            ? 'text-gray-300 hover:bg-gray-700 hover:text-white' 
                            : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                          }
                        `}
                      >
                        <Settings className="mr-3 h-4 w-4" />
                        Settings
                      </button> */}

                      <button
                        onClick={() => handleProfileMenuClick('subscribed')}
                        className={`
                          w-full flex items-center px-4 py-2 text-sm font-semibold transition-all duration-150
                          ${'text-[color:var(--app-primary-mid)] hover:bg-[color:var(--app-primary-soft)] hover:text-[color:var(--app-primary-deep)]'
                          }
                        `}
                      >
                        <Crown className="mr-3 h-4 w-4 text-amber-400" />
                        Upgrade to Pro
                      </button>
                      <div className={`border-t my-1 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}></div>
                      
                      <button
                        onClick={() => handleProfileMenuClick('logout')}
                        className={`
                          w-full flex items-center px-4 py-2 text-sm transition-colors
                          ${isDarkMode 
                            ? 'text-red-400 hover:bg-gray-700 hover:text-red-300' 
                            : 'text-red-600 hover:bg-gray-100 hover:text-red-700'
                          }
                        `}
                      >
                        <LogOut className="mr-3 h-4 w-4" />
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <button
                onClick={() => navigate('/login')}
                className={`
                  flex items-center justify-center p-1.5 rounded-md transition-colors duration-150 group
                  ${isDarkMode 
                    ? 'text-gray-300 hover:bg-gray-700 hover:text-white' 
                    : 'text-gray-600 hover:bg-gray-100 hover:text-gray-800'
                  }
                  w-10 h-10
                `}
                title="Login"
                aria-label="Login"
              >
                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold border border-transparent group-hover:border-current transition-all duration-200 ${
                  isDarkMode ? 'bg-gray-600' : 'bg-gray-500'
                }`}>
                  ?
                </div>
              </button>
            )}
          </div>
        </div>
      </div>
      {/* Google Calendar Modal */}
      {showCalendarModal && (
        <GoogleCalendarModal isOpen={showCalendarModal} onClose={() => setShowCalendarModal(false)} />
      )}
      {/* Upgrade Modal */}
      <UpgradeModal isOpen={showUpgradeModal} onClose={() => setShowUpgradeModal(false)} isDarkMode={isDarkMode} />
      {/* Domains editing moved to Onboarding — UI entry removed */}
    </>
  );
}
